// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

export enum ArenaMode {
  Mode2POnline = "2P_ONLINE",         // random 2 players
  Mode2PWithFriends = "2P_WITH_FRIENDS", // private 2 players
  Mode4POnline = "4P_ONLINE",         // random 4 players
  Mode4PWithFriends = "4P_WITH_FRIENDS"  // private 4 players
}

export enum Rank {
  Winner = 1,  // 1st place
  Second = 2,  // 2nd place
  Third = 3,   // 3rd place
  Fourth = 4   // 4th place
}

export enum CurrencyType {
  Coin = 1,
  LudoCoin = 2,
  QuizCoin = 3,
  SolitaireCoin = 4,
}

export interface Currency {
  type: CurrencyType;
  amount: number;
}

export interface ArenaReward {
  type: CurrencyType;
  amount: number;
}

export interface Arena {
  name: string;
  mode: ArenaMode;
  fee_cost: Currency;
  rewards: Record<string, ArenaReward[]>;
  consolation_reward: ArenaReward[];
  json_config: Record<string, any>;
}

export interface LudoConfiguration {
  arenas: Record<string, Arena>;
}

// Helper functions for enum display
export const getArenaModeName = (mode: ArenaMode): string => {
  switch (mode) {
    case ArenaMode.Mode2POnline:
      return "2P Online (Random)";
    case ArenaMode.Mode2PWithFriends:
      return "2P With Friends (Private)";
    case ArenaMode.Mode4POnline:
      return "4P Online (Random)";
    case ArenaMode.Mode4PWithFriends:
      return "4P With Friends (Private)";
    default:
      return mode;
  }
};

export const getCurrencyTypeName = (type: CurrencyType): string => {
  switch (type) {
    case CurrencyType.Coin:
      return "Coin";
    case CurrencyType.LudoCoin:
      return "Ludo Coin";
    case CurrencyType.QuizCoin:
      return "Quiz Coin";
    case CurrencyType.SolitaireCoin:
      return "Solitaire Coin";
    default:
      return `Type ${type}`;
  }
};

export const getRankName = (rank: Rank): string => {
  switch (rank) {
    case Rank.Winner:
      return "1st Place (Winner)";
    case Rank.Second:
      return "2nd Place";
    case Rank.Third:
      return "3rd Place";
    case Rank.Fourth:
      return "4th Place";
    default:
      return `Rank ${rank}`;
  }
};

// Validation functions
export const isValidArenaMode = (mode: string): mode is ArenaMode => {
  return Object.values(ArenaMode).includes(mode as ArenaMode);
};

export const isValidCurrencyType = (type: number): type is CurrencyType => {
  return Object.values(CurrencyType).includes(type as CurrencyType);
};

export const isValidRank = (rank: number): rank is Rank => {
  return Object.values(Rank).includes(rank as Rank);
};

// Default configuration generator
export const createDefaultArena = (name: string, mode: ArenaMode): Arena => {
  const isMultiplayer = mode === ArenaMode.Mode4POnline || mode === ArenaMode.Mode4PWithFriends;
  
  return {
    name,
    mode,
    fee_cost: {
      type: CurrencyType.LudoCoin,
      amount: 100
    },
    rewards: isMultiplayer ? {
      "1": [{ type: CurrencyType.LudoCoin, amount: 300 }],
      "2": [{ type: CurrencyType.LudoCoin, amount: 60 }],
      "3": [{ type: CurrencyType.LudoCoin, amount: 40 }]
    } : {
      "1": [{ type: CurrencyType.LudoCoin, amount: 160 }],
      "2": [{ type: CurrencyType.LudoCoin, amount: 40 }]
    },
    consolation_reward: [],
    json_config: {}
  };
};
